import { useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { Plus, Upload } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { EntityIndexLayout, WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { PricebookCSVImportDialog } from '@/components/organisms/pricebook/PricebookCSVImportDialog';
import {
  DEFAULT_PRICEBOOK_LIST_FILTERS,
  PRICEBOOK_SORT_OPTION_COUNT,
  PRICEBOOK_TYPE_FILTER_VALUES,
  PricebookListPanel,
  pricebookFiltersForAccess,
  pricebookListParamsForTypeFilter,
  PRICEBOOK_ARCHIVED_FILTER_PERMISSIONS,
  type PricebookListFilters,
  type PricebookTypeFilter,
} from '@/components/organisms/pricebook/PricebookListPanel';
import { PricebookTableView } from '@/components/organisms/pricebook/PricebookTableView';
import { usePricebookItems } from '@/hooks/pricebook/usePricebookItems';
import { useHasAnyPermission, useIsFieldHidden } from '@/hooks/usePermissions';
import type { PricebookItem } from '@/types/pricebook';
import { PricebookCategoriesLink } from './pricebook/-categories-navigation';

const NUMBER_FILTER_OPS = ['eq', 'gt', 'lt', 'gte', 'lte'] as const;
const PRICE_TYPE_FILTERS = ['all', 'flat', 'hourly'] as const;
const TAXABLE_FILTERS = ['all', 'yes', 'no'] as const;

export const Route = createFileRoute('/_dashboard/dashboard/pricebook')({
  validateSearch: (search: Record<string, unknown>): PricebookListFilters => {
    const sort = Number(search.sort);
    return {
      q: typeof search.q === 'string' ? search.q : DEFAULT_PRICEBOOK_LIST_FILTERS.q,
      type: PRICEBOOK_TYPE_FILTER_VALUES.includes(search.type as PricebookTypeFilter)
        ? (search.type as PricebookTypeFilter)
        : DEFAULT_PRICEBOOK_LIST_FILTERS.type,
      sort:
        Number.isFinite(sort) && sort >= 0
          ? Math.min(Math.floor(sort), PRICEBOOK_SORT_OPTION_COUNT - 1)
          : DEFAULT_PRICEBOOK_LIST_FILTERS.sort,
      sku: typeof search.sku === 'string' ? search.sku : DEFAULT_PRICEBOOK_LIST_FILTERS.sku,
      priceOp: NUMBER_FILTER_OPS.includes(search.priceOp as PricebookListFilters['priceOp'])
        ? (search.priceOp as PricebookListFilters['priceOp'])
        : DEFAULT_PRICEBOOK_LIST_FILTERS.priceOp,
      priceValue:
        typeof search.priceValue === 'string'
          ? search.priceValue
          : DEFAULT_PRICEBOOK_LIST_FILTERS.priceValue,
      costOp: NUMBER_FILTER_OPS.includes(search.costOp as PricebookListFilters['costOp'])
        ? (search.costOp as PricebookListFilters['costOp'])
        : DEFAULT_PRICEBOOK_LIST_FILTERS.costOp,
      costValue:
        typeof search.costValue === 'string'
          ? search.costValue
          : DEFAULT_PRICEBOOK_LIST_FILTERS.costValue,
      priceType: PRICE_TYPE_FILTERS.includes(search.priceType as PricebookListFilters['priceType'])
        ? (search.priceType as PricebookListFilters['priceType'])
        : DEFAULT_PRICEBOOK_LIST_FILTERS.priceType,
      businessUnit:
        typeof search.businessUnit === 'string'
          ? search.businessUnit
          : DEFAULT_PRICEBOOK_LIST_FILTERS.businessUnit,
      categoryId:
        typeof search.categoryId === 'string'
          ? search.categoryId
          : DEFAULT_PRICEBOOK_LIST_FILTERS.categoryId,
      unit: typeof search.unit === 'string' ? search.unit : DEFAULT_PRICEBOOK_LIST_FILTERS.unit,
      taxable: TAXABLE_FILTERS.includes(search.taxable as PricebookListFilters['taxable'])
        ? (search.taxable as PricebookListFilters['taxable'])
        : DEFAULT_PRICEBOOK_LIST_FILTERS.taxable,
      glAccount:
        typeof search.glAccount === 'string'
          ? search.glAccount
          : DEFAULT_PRICEBOOK_LIST_FILTERS.glAccount,
      dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
      dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
      updatedFrom: typeof search.updatedFrom === 'string' ? search.updatedFrom : undefined,
      updatedTo: typeof search.updatedTo === 'string' ? search.updatedTo : undefined,
      tagIds: normalizeTagIds(search.tagIds),
    };
  },
  component: PricebookLayout,
});

function normalizeTagIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim() !== '');
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function cleanPricebookSearch(next: Record<string, unknown>): PricebookListFilters {
  if (!next.q) delete next.q;
  if (next.type === DEFAULT_PRICEBOOK_LIST_FILTERS.type) delete next.type;
  if (next.sort === DEFAULT_PRICEBOOK_LIST_FILTERS.sort) delete next.sort;
  if (!next.sku) delete next.sku;
  if (next.priceOp === DEFAULT_PRICEBOOK_LIST_FILTERS.priceOp) delete next.priceOp;
  if (!next.priceValue) delete next.priceValue;
  if (next.costOp === DEFAULT_PRICEBOOK_LIST_FILTERS.costOp) delete next.costOp;
  if (!next.costValue) delete next.costValue;
  if (next.priceType === DEFAULT_PRICEBOOK_LIST_FILTERS.priceType) delete next.priceType;
  if (!next.businessUnit) delete next.businessUnit;
  if (!next.categoryId) delete next.categoryId;
  if (!next.unit) delete next.unit;
  if (next.taxable === DEFAULT_PRICEBOOK_LIST_FILTERS.taxable) delete next.taxable;
  if (!next.glAccount) delete next.glAccount;
  if (!next.dateFrom) delete next.dateFrom;
  if (!next.dateTo) delete next.dateTo;
  if (!next.updatedFrom) delete next.updatedFrom;
  if (!next.updatedTo) delete next.updatedTo;
  if (Array.isArray(next.tagIds) && next.tagIds.length === 0) delete next.tagIds;
  return next as unknown as PricebookListFilters;
}

function PricebookLayout() {
  const { location } = useRouterState();
  const router = useRouter();
  const search = Route.useSearch();
  const canSeeArchived = useHasAnyPermission(...PRICEBOOK_ARCHIVED_FILTER_PERMISSIONS);
  const profitabilityHidden = useIsFieldHidden('PROFITABILITY');
  // A filter this user cannot have is read as its default — see
  // `pricebookFiltersForAccess`, which owns that rule for the whole set.
  const filters = useMemo<PricebookListFilters>(
    () => pricebookFiltersForAccess(search, { canSeeArchived, profitabilityHidden }),
    [search, canSeeArchived, profitabilityHidden],
  );

  const [importOpen, setImportOpen] = useState(false);

  const idMatch = location.pathname.match(/\/dashboard\/pricebook\/([^/]+)/);
  const selectedId = idMatch ? idMatch[1] : undefined;
  const hasChildRoute = !/^\/dashboard\/pricebook\/?$/.test(location.pathname);
  const isIndexRoute = /^\/dashboard\/pricebook\/?$/.test(location.pathname);
  const isFormRoute = /\/dashboard\/pricebook\/(?:new|[^/]+\/edit)\/?$/.test(location.pathname);
  // NOT-590 — the category tree manages the whole catalog at once, so it owns
  // the full width instead of sitting in the item split's detail pane.
  const isCategoriesRoute = /\/dashboard\/pricebook\/categories\/?$/.test(location.pathname);

  const itemsQuery = usePricebookItems({
    search: filters.q || undefined,
    ...pricebookListParamsForTypeFilter(filters.type),
    tagIds: filters.tagIds.length > 0 ? filters.tagIds : undefined,
    categoryId: filters.categoryId || undefined,
    limit: 100,
  });
  const items = useMemo(() => itemsQuery.data?.items ?? [], [itemsQuery.data?.items]);

  useEffect(() => {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    if (!isDesktop || !isIndexRoute) return;
    try {
      if (localStorage.getItem('pricebook-view') === 'table') return;
    } catch {
      /* no-op */
    }
    if (items.length === 0) return;
    void router.navigate({
      to: '/dashboard/pricebook/$id',
      params: { id: items[0].id },
      search: {} as never,
      replace: true,
    });
  }, [isIndexRoute, items, router]);

  function handleSelect(item: PricebookItem) {
    void router.navigate({
      to: '/dashboard/pricebook/$id',
      params: { id: item.id },
      search: (prev) => prev as never,
    });
  }

  function handleFiltersChange(patch: Partial<PricebookListFilters>) {
    void router.navigate({
      search: (prev) => {
        const next = { ...prev, ...patch } as Record<string, unknown>;
        return cleanPricebookSearch(next) as never;
      },
      replace: true,
    });
  }

  const handleNewItem = useCallback(
    (inSheet = false) => {
      void router.navigate({
        to: '/dashboard/pricebook/new',
        search: { type: 'service' as const, ...(inSheet ? { inSheet: true } : {}) } as never,
      });
    },
    [router],
  );

  const handleHeaderNewItem = useCallback(() => handleNewItem(), [handleNewItem]);
  const handleOpenImport = useCallback(() => setImportOpen(true), []);

  if (isCategoriesRoute) {
    return (
      <WorkspaceShell>
        <PricebookHeaderSlot hidden onNewItem={() => {}} onImport={() => {}} />
        <Outlet />
      </WorkspaceShell>
    );
  }

  return (
    <WorkspaceShell hideHeader={isFormRoute}>
      <PricebookHeaderSlot
        hidden={isFormRoute}
        onNewItem={handleHeaderNewItem}
        onImport={handleOpenImport}
      />
      <EntityIndexLayout
        title="Pricebook"
        storageKey="pricebook-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() =>
          void router.navigate({ to: '/dashboard/pricebook', search: {} as never })
        }
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        splitWidthClass="w-[360px]"
        tableSheetWidthClass="w-[620px] sm:max-w-[620px]"
        hideListInSplit={isFormRoute}
        fullPageFormInTable
        splitList={() => (
          <div className="flex h-full flex-col">
            <PricebookListPanel
              selectedId={selectedId}
              onSelect={handleSelect}
              filters={filters}
              onFiltersChange={handleFiltersChange}
            />
          </div>
        )}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <PricebookTableView
            onView={(item) =>
              openInSheet(
                () =>
                  void router.navigate({
                    to: '/dashboard/pricebook/$id',
                    params: { id: item.id },
                    search: {} as never,
                  }),
              )
            }
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="md:hidden h-full flex flex-col">
            {isIndexRoute ? (
              <PricebookListPanel
                selectedId={selectedId}
                onSelect={handleSelect}
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            ) : (
              <Outlet />
            )}
          </div>
        }
      />
      <PricebookCSVImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </WorkspaceShell>
  );
}

function PricebookHeaderSlot({
  hidden,
  onNewItem,
  onImport,
}: {
  hidden: boolean;
  onNewItem: () => void;
  onImport: () => void;
}) {
  const toolbarActions = useMemo(() => {
    if (hidden) return null;
    return (
      <>
        <PricebookCategoriesLink />
        <Button variant="outline" size="sm" onClick={onImport} title="Import CSV">
          <Upload size={14} />
          Import
        </Button>
      </>
    );
  }, [hidden, onImport]);

  const newButton = useMemo(() => {
    if (hidden) return null;
    return (
      <Button size="sm" title="Add item" onClick={onNewItem}>
        <Plus size={14} />
        Add item
      </Button>
    );
  }, [hidden, onNewItem]);

  useWorkspaceHeader({ toolbarActions, newButton }, [toolbarActions, newButton]);
  return null;
}
